<template>
  <div style="max-width: 90%; margin: auto" class="py-10">
    <!-- Header -->
    <div class="d-flex justify-space-between align-center mb-8 flex-wrap" style="gap: 12px">
      <div>
        <p class="page-subtitle mb-1">Verwaltung</p>
        <h1 class="page-title">Benutzerdetails</h1>
      </div>
      <div class="d-flex" style="gap: 10px">
        <button class="secondary-btn" @click="$router.push('/employees')">
          <v-icon size="16">mdi-arrow-left</v-icon>
          <span>Zurück</span>
        </button>
        <button class="primary-btn" @click="$router.push(`/employee/edit/${$route.params.id}`)">
          <v-icon size="16">mdi-pencil-outline</v-icon>
          <span>Bearbeiten</span>
        </button>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="loading-state">
      <div class="skel-card skel-big"></div>
    </div>

    <!-- Error -->
    <div v-else-if="loadError" class="error-card">
      <v-icon size="40" color="#D32F2F" class="mb-3">mdi-alert-circle-outline</v-icon>
      <div class="error-title">Fehler beim Laden</div>
      <p class="error-desc">{{ loadError }}</p>
      <button class="secondary-btn mt-4" @click="loadEmployee">
        <v-icon size="16">mdi-refresh</v-icon>
        Erneut versuchen
      </button>
    </div>

    <!-- Content -->
    <div v-else class="grid-split">
      <!-- Linke Spalte: Benutzerdaten + Berechtigungen gestapelt -->
      <div class="left-stack">
      <!-- Info -->
      <div class="card-modern">
        <h2 class="section-title mb-5">Benutzerdaten</h2>

        <div class="info-list">
          <div class="info-item">
            <div class="info-icon"><v-icon size="16" color="#5B5BD6">mdi-account-outline</v-icon></div>
            <div class="info-body">
              <div class="info-label">Vollständiger Name</div>
              <div class="info-value">{{ fullName }}</div>
            </div>
          </div>

          <div class="info-item">
            <div class="info-icon"><v-icon size="16" color="#2E7D32">mdi-email-outline</v-icon></div>
            <div class="info-body">
              <div class="info-label">E-Mail</div>
              <div class="info-value">{{ employee.email || '—' }}</div>
            </div>
          </div>

          <div class="info-item">
            <div class="info-icon"><v-icon size="16" color="#E65100">mdi-briefcase-outline</v-icon></div>
            <div class="info-body">
              <div class="info-label">Position</div>
              <div class="info-value">{{ employee.position || 'Keine Position angegeben' }}</div>
            </div>
          </div>

          <div class="info-item">
            <div class="info-icon"><v-icon size="16" color="#7C4DFF">mdi-calendar-clock</v-icon></div>
            <div class="info-body">
              <div class="info-label">Account erstellt am</div>
              <div class="info-value">{{ formattedCreatedAt }}</div>
            </div>
          </div>

        </div>
      </div>

      <!-- Berechtigungen (innerhalb der linken Spalte, unter Benutzerdaten) -->
      <div class="card-modern perm-card">
        <h2 class="section-title mb-2">
          <v-icon size="18" color="#0a1628" class="mr-2">mdi-shield-check-outline</v-icon>
          Berechtigungen
        </h2>
        <div class="d-flex align-center mb-5" style="gap: 8px; flex-wrap: wrap">
          <span class="text-caption" style="color: #888">Aktive Rolle:</span>
          <span class="preset-badge" :class="{ 'preset-badge-custom': !activePresetIsSystem }">
            <v-icon size="11" :color="activePresetIsSystem ? '#5B5BD6' : '#0a1628'">
              {{ activePresetIsSystem ? 'mdi-shield-star-outline' : (activePresetName ? 'mdi-bookmark-outline' : 'mdi-tune-vertical') }}
            </v-icon>
            {{ activePresetName || 'Individuelle Auswahl' }}
          </span>
          <span class="text-caption" style="color: #c5cbd3; margin: 0 4px">·</span>
          <span class="text-caption" style="color: #888">
            {{ activePermissionCount }} von {{ totalPermissionCount }} Rechten aktiv
          </span>
        </div>

        <!-- Reviewer-Rollen separat -->
        <div v-if="employee.isTechnicalReviewer || employee.isVisualReviewer" class="perm-module">
          <div class="perm-module-header">
            <v-icon size="14" color="#5B5BD6">mdi-clipboard-check-outline</v-icon>
            <span>Review-Rollen</span>
          </div>
          <div class="perm-row">
            <span v-if="employee.isTechnicalReviewer" class="perm-pill perm-pill-active">
              <v-icon size="11">mdi-code-tags</v-icon>
              Technischer Reviewer
            </span>
            <span v-if="employee.isVisualReviewer" class="perm-pill perm-pill-active">
              <v-icon size="11">mdi-eye-outline</v-icon>
              Optischer Reviewer
            </span>
          </div>
        </div>

        <!-- Permission-Matrix nach Modul (read-only) -->
        <div v-for="m in modules" :key="m.key" class="perm-module">
          <div class="perm-module-header">
            <v-icon size="14" color="#0a1628">{{ m.icon }}</v-icon>
            <span>{{ m.label }}</span>
            <span v-if="moduleActiveCount(m) === 0" class="perm-empty-tag">Keine Rechte</span>
          </div>
          <div v-if="moduleActiveCount(m) > 0" class="perm-row">
            <span
              v-for="a in m.actions"
              :key="`${m.key}.${a}`"
              v-show="isPermActive(m.key, a)"
              class="perm-pill perm-pill-active"
            >
              <v-icon size="11">{{ permIcon(a) }}</v-icon>
              {{ actionLabels[a] || a }}
            </span>
          </div>
        </div>
      </div>
      </div>
      <!-- /left-stack -->

      <!-- Avatar -->
      <div class="card-modern avatar-side">
        <h2 class="section-title mb-4">
          <v-icon size="18" color="#0a1628" class="mr-2">mdi-account-circle-outline</v-icon>
          Avatar
        </h2>

        <div class="avatar-preview">
          <v-img
            :src="`/api/employees/${$route.params.id}/image`"
            cover
            aspect-ratio="1"
          >
            <template #placeholder>
              <div class="avatar-placeholder">
                <v-progress-circular indeterminate size="30" width="2" color="#c5cbd3" />
              </div>
            </template>
            <template #error>
              <div class="avatar-placeholder">
                <v-icon size="56" color="#c5cbd3" class="mb-3">mdi-account-circle</v-icon>
                <div class="avatar-fallback-text">Kein Avatar vorhanden</div>
              </div>
            </template>
          </v-img>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'EmployeeDetails',
  data() {
    return {
      employee: {},
      loading: true,
      loadError: null,
      modules: [],
      actionLabels: {},
      presets: [],
    };
  },

  computed: {
    fullName() {
      if (!this.employee) return '';
      return `${this.employee.firstname || ''} ${this.employee.lastname || ''}`.trim();
    },
    formattedCreatedAt() {
      if (!this.employee?.created_at) return 'Unbekannt';
      try {
        return new Date(this.employee.created_at).toLocaleDateString('de-DE', {
          day: '2-digit', month: '2-digit', year: 'numeric',
        });
      } catch (e) {
        return 'Ungültiges Datum';
      }
    },
    normalizedPerms() {
      const src = this.employee?.permissions || {};
      const out = {};
      for (const k of Object.keys(src)) out[k] = src[k] === 1 || src[k] === true ? 1 : 0;
      return out;
    },
    matchingPreset() {
      const current = this.normalizedPerms;
      const tech = this.employee?.isTechnicalReviewer ? 1 : 0;
      const vis = this.employee?.isVisualReviewer ? 1 : 0;
      for (const p of this.presets) {
        const presetPerms = {};
        const pp = p.permissions || {};
        for (const k of Object.keys(pp)) presetPerms[k] = pp[k] === 1 || pp[k] === true ? 1 : 0;
        const keys = new Set([...Object.keys(current), ...Object.keys(presetPerms)]);
        let equal = true;
        for (const k of keys) {
          if ((current[k] || 0) !== (presetPerms[k] || 0)) { equal = false; break; }
        }
        if (equal && (p.isTechnicalReviewer ? 1 : 0) === tech && (p.isVisualReviewer ? 1 : 0) === vis) {
          return p;
        }
      }
      return null;
    },
    activePresetName() { return this.matchingPreset?.name || null; },
    activePresetIsSystem() { return !!this.matchingPreset?.isSystem; },
    activePermissionCount() {
      return Object.values(this.normalizedPerms).filter(Boolean).length;
    },
    totalPermissionCount() {
      let n = 0;
      for (const m of this.modules) n += m.actions.length;
      return n;
    },
  },

  mounted() {
    this.loadEmployee();
    this.loadPermissionSchema();
    this.loadPresets();
  },

  methods: {
    async loadEmployee() {
      this.loading = true;
      this.loadError = null;
      try {
        const result = await axios.get('/api/employees/get', { params: { id: this.$route.params.id } });
        const emp = result.data || {};
        if (emp.permissions && typeof emp.permissions === 'string') {
          try { emp.permissions = JSON.parse(emp.permissions); } catch (_e) { emp.permissions = {}; }
        }
        emp.permissions = emp.permissions || {};
        this.employee = emp;
      } catch (e) {
        console.error('Fehler beim Laden!', e);
        this.loadError = 'Der Benutzer konnte nicht geladen werden. Bitte versuche es später erneut.';
      } finally {
        this.loading = false;
      }
    },
    async loadPermissionSchema() {
      try {
        const res = await axios.get('/api/permissions/keys');
        this.modules = res.data.data.modules || [];
        this.actionLabels = res.data.data.actionLabels || {};
      } catch (e) {
        console.error('Permission-Schema fehlgeschlagen', e);
      }
    },
    async loadPresets() {
      try {
        const res = await axios.get('/api/permissions/presets');
        this.presets = res.data.data || [];
      } catch (e) {
        console.error('Presets fehlgeschlagen', e);
      }
    },
    isPermActive(moduleKey, action) {
      return this.normalizedPerms[`${moduleKey}.${action}`] === 1;
    },
    moduleActiveCount(m) {
      return m.actions.filter((a) => this.isPermActive(m.key, a)).length;
    },
    permIcon(action) {
      return ({
        view: 'mdi-eye-outline',
        create: 'mdi-plus-circle-outline',
        edit: 'mdi-pencil-outline',
        delete: 'mdi-delete-outline',
        respond: 'mdi-message-reply-text-outline',
      })[action] || 'mdi-checkbox-marked-circle-outline';
    },
  },
};
</script>

<style scoped>
.page-title { font-size: 26px; font-weight: 800; color: #0a1628; letter-spacing: -0.3px; margin-bottom: 6px; }
.page-subtitle { font-size: 13px; color: #888; }

.primary-btn {
  display: inline-flex; align-items: center; gap: 8px;
  padding: 10px 18px; background: #0a1628; color: white;
  border: none; border-radius: 10px; font-size: 13px; font-weight: 700;
  cursor: pointer; transition: background 0.15s; font-family: inherit;
}
.primary-btn:hover { background: #1d3457; }

.secondary-btn {
  display: inline-flex; align-items: center; gap: 8px;
  padding: 10px 18px; background: white; color: #0a1628;
  border: 1px solid #eceef2; border-radius: 10px; font-size: 13px; font-weight: 600;
  cursor: pointer; transition: border-color 0.15s; font-family: inherit;
}
.secondary-btn:hover { border-color: #0a1628; }

.grid-split { display: grid; grid-template-columns: minmax(0, 2fr) minmax(0, 1fr); gap: 20px; align-items: start; }

.card-modern { background: white; border: 1px solid #eceef2; border-radius: 16px; padding: 28px; }
.section-title {
  font-size: 16px; font-weight: 700; color: #0a1628;
  display: flex; align-items: center;
}

/* Info list */
.info-list { display: flex; flex-direction: column; }
.info-item {
  display: flex; align-items: flex-start; gap: 14px;
  padding: 16px 0; border-bottom: 1px solid #f5f5f5;
}
.info-item:last-child { border-bottom: none; }
.info-icon {
  width: 36px; height: 36px; min-width: 36px; border-radius: 10px;
  background: #f8f9fb; display: flex; align-items: center; justify-content: center;
}
.info-body { flex: 1; min-width: 0; }
.info-label { font-size: 11px; font-weight: 600; color: #999; text-transform: uppercase; letter-spacing: 0.3px; margin-bottom: 4px; }
.info-value { font-size: 14px; font-weight: 600; color: #0a1628; word-break: break-word; }
.info-roles { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 2px; }

.badge-role {
  display: inline-flex; align-items: center; gap: 4px;
  padding: 4px 10px; border-radius: 12px;
  font-size: 11px; font-weight: 700;
}
.badge-tech { background: rgba(91,91,214,0.1); color: #5B5BD6; }
.badge-visual { background: rgba(46,125,50,0.1); color: #2E7D32; }

/* Avatar */
.avatar-side { position: sticky; top: 24px; }
.avatar-preview {
  border-radius: 12px; overflow: hidden;
  background: #f8f9fb; border: 1px solid #eceef2;
  padding: 8px;
}
.avatar-preview :deep(.v-img) { border-radius: 8px; overflow: hidden; }
.avatar-placeholder {
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  aspect-ratio: 1; background: white; text-align: center;
}
.avatar-fallback-text { font-size: 12px; color: #888; }

/* Error */
.error-card {
  background: white; border: 1px solid rgba(211,47,47,0.2); border-radius: 16px;
  padding: 48px 32px; text-align: center;
  display: flex; flex-direction: column; align-items: center;
}
.error-title { font-size: 16px; font-weight: 700; color: #D32F2F; margin-bottom: 6px; }
.error-desc { font-size: 13px; color: #888; max-width: 420px; }

/* Skeleton */
.loading-state { }
.skel-card {
  background: linear-gradient(90deg, #eceef2 25%, #f5f6f8 50%, #eceef2 75%);
  background-size: 200% 100%; animation: shimmer 1.5s infinite; border-radius: 16px;
}
.skel-big { height: 420px; }
@keyframes shimmer { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }

@media (max-width: 960px) {
  .grid-split { grid-template-columns: 1fr; }
  .avatar-side { position: static; }
}

/* === Linke Spalte: Benutzerdaten + Berechtigungen gestapelt === */
.left-stack {
  display: flex;
  flex-direction: column;
  gap: 20px;
  min-width: 0;
}

/* === Permissions Card === */
.perm-card { /* sitzt jetzt direkt im left-stack, kein margin-top mehr nötig */ }
.preset-badge {
  display: inline-flex; align-items: center; gap: 5px;
  padding: 4px 10px;
  background: rgba(91,91,214,0.08);
  border: 1px solid rgba(91,91,214,0.18);
  border-radius: 6px;
  font-size: 11.5px; font-weight: 700; color: #5B5BD6;
  letter-spacing: -0.05px;
}
.preset-badge-custom {
  background: rgba(10,22,40,0.04);
  border-color: rgba(10,22,40,0.12);
  color: #0a1628;
}

.perm-module {
  margin-bottom: 14px;
}
.perm-module:last-child { margin-bottom: 0; }
.perm-module-header {
  display: flex; align-items: center; gap: 6px;
  font-size: 12px; font-weight: 700; color: #0a1628;
  margin-bottom: 8px;
  padding-bottom: 6px;
  border-bottom: 1px solid #eceef2;
  letter-spacing: -0.1px;
}
.perm-empty-tag {
  margin-left: auto;
  font-size: 10px; font-weight: 600; color: #c5cbd3;
  letter-spacing: 0.3px; text-transform: uppercase;
}
.perm-row {
  display: flex; flex-wrap: wrap; gap: 6px;
}
.perm-pill {
  display: inline-flex; align-items: center; gap: 5px;
  padding: 5px 11px;
  background: white;
  border: 1px solid #eceef2;
  border-radius: 999px;
  font-size: 11.5px; font-weight: 600; color: #6b7280;
}
.perm-pill.perm-pill-active {
  background: #0a1628;
  border-color: #0a1628;
  color: white;
}
</style>
